import { Routes } from '@angular/router';
import { AuthGuard } from '@lib/core/auth/guards/auth.guard';
import { Error404Component } from './core/error-404/error-404.component';
import { NoAuthGuard } from '@lib/core';
import { LayoutComponent } from './core/layout/layout.component';
import { AuthResolver } from './auth/auth.resolver';

export const routes: Routes = [
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

  // Top level and unguarded on purpose: emailed to the person being opted out, who
  // may be a family member with no account - and the `auth` parent's NoAuthGuard
  // would bounce a signed-in customer away from their own confirmation.
  {
    path: 'breach-confirmation/:token',
    loadChildren: () =>
      import('./removal-requests/breach-confirmation/breach-confirmation.module').then(
        m => m.BreachConfirmationModule
      ),
  },

  {
    path: 'privacy-meter',
    loadChildren: () =>
      import('./privacy-meter/privacy-meter.module').then(
        m => m.PrivacyMeterModule
      ),
  },
  // Checkout creation for authenticated users (from guest checkout redirect)
  {
    path: 'checkout/create',
    loadChildren: () =>
      import('./checkout/checkout-create/checkout-create.module').then(
        m => m.CheckoutCreateModule
      ),
    canActivate: [AuthGuard], // Requires authentication
  },
  // Guest checkout - standalone layout without LayoutComponent wrapper
  {
    path: 'checkout/guest',
    loadChildren: () =>
      import('./checkout/guest-checkout.module').then(
        m => m.GuestCheckoutModule
      ),
    canActivate: [NoAuthGuard], // Prevent logged-in users from accessing guest checkout
  },
  {
    path: '',
    component: LayoutComponent,
    resolve: {
      user: AuthResolver,
    },
    children: [
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./dashboard/dashboard.module').then(m => m.DashboardModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'privacy-check',
        loadChildren: () =>
          import('./privacy-check/privacy-check.module').then(
            m => m.PrivacyCheckModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./profile/profile.module').then(m => m.ProfileModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'billing',
        loadChildren: () =>
          import('./billing/billing.module').then(m => m.BillingModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('./notifications/notifications.module').then(
            m => m.NotificationsModule
          ),
        canActivate: [AuthGuard],
      },

      // CHECKOUT (authenticated routes only)

      {
        path: 'checkout/:id',
        loadChildren: () =>
          import('./checkout/checkout.module').then(m => m.CheckoutModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'checkout/:id/status',
        loadChildren: () =>
          import('./checkout/checkout.module').then(m => m.CheckoutModule),
        canActivate: [AuthGuard],
      },
      // REMOVAL REQUESTS
      {
        path: 'removals',
        children: [
          // The mixed-type list lived here. It routed every row to the data
          // breach wizard regardless of type, so a search engine draft could be
          // submitted through the wrong flow. Each type has its own page now.
          {
            path: '',
            redirectTo: '/dashboard',
            pathMatch: 'full',
          },

          // Custom Removal
          {
            path: 'custom-removal',
            loadChildren: () =>
              import('./removal-requests/custom-removal/custom-removal.module').then(
                m => m.CustomRemovalModule
              ),
            canActivate: [AuthGuard],
          },

          // Databrokers
          {
            path: 'data-brokers',
            loadChildren: () =>
              import('./removal-requests/data-brokers/data-brokers.module').then(
                m => m.DataBrokersModule
              ),
            canActivate: [AuthGuard],
          },
          // Search Engine
          {
            path: 'search-engine',
            loadChildren: () =>
              import('./removal-requests/search-engine/search-engine.module').then(
                m => m.SearchEngineModule
              ),
            canActivate: [AuthGuard],
          },
          // // Doxbin
          // {
          //   path: 'doxbin',
          //   loadChildren: () =>
          //     import('./removal-requests/doxbin/doxbin.module').then(
          //       (m) => m.DoxbinModule,
          //     ),
          //   canActivate: [AuthGuard],
          // },
          // // Kyc
          // {
          //   path: 'kyc',
          //   loadChildren: () =>
          //     import('./removal-requests/kyc/kyc.module').then(
          //       (m) => m.KycModule,
          //     ),
          //   canActivate: [AuthGuard],
          // },
          // Data Breach (unified: recurring + one-time share the plan + members flow)
          {
            path: 'data-breach',
            loadChildren: () =>
              import('./removal-requests/data-breach-subscription/data-breach-subscription.module').then(
                m => m.DataBreachSubscriptionModule
              ),
            canActivate: [AuthGuard],
          },
          {
            path: 'data-breach-subscription',
            redirectTo: 'data-breach',
            pathMatch: 'prefix',
          },
          // Old links from the removed list. Only custom removals were reached
          // this way. Keep last so it can't shadow the named children above.
          {
            path: ':id/details',
            redirectTo: 'custom-removal/:id/details',
          },
        ],
      },
      // REPORTS
      {
        path: 'reports',
        loadChildren: () =>
          import('./reports/reports.module').then(m => m.ReportsModule),
        canActivate: [AuthGuard],
      },
      // Rewards
      {
        path: 'rewards',
        loadChildren: () =>
          import('./rewards/rewards.module').then(m => m.RewardsModule),
        canActivate: [AuthGuard],
      },
      // Contact Us
      {
        path: 'contact-us',
        loadChildren: () =>
          import('./contact-us/contact-us.module').then(m => m.ContactUsModule),
        canActivate: [AuthGuard],
      },
    ],
  },
  //Auth Routes
  {
    path: 'auth',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    children: [
      {
        path: 'login',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        loadChildren: () =>
          import('./auth/login/login.module').then(m => m.LoginModule),
      },
      {
        path: 'register',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        loadChildren: () =>
          import('./auth/register/register.module').then(m => m.RegisterModule),
      },
      {
        path: 'password/forgot',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        loadChildren: () =>
          import('./auth/forgot-password/forgot-password.module').then(
            m => m.ForgotPasswordModule
          ),
      },
      {
        path: 'password/reset/:code',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        loadChildren: () =>
          import('./auth/reset-password/reset-password.module').then(
            m => m.ResetPasswordModule
          ),
      },
      {
        path: 'invite/accept/:code',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        loadChildren: () =>
          import('./auth/invite-accept/invite-accept.module').then(
            m => m.InviteAcceptModule
          ),
      },
    ],
  },

  // Redirect to login if no matching route is found
  { path: '**', component: Error404Component },
];
